import isEqual from "lodash.isequal";
import { useCallback, useEffect, useReducer } from "react";
import type { Activity } from "@/hooks/api/use-account-activities";
import type { CollapsedActivity } from "@/pages/accounts/account-explorer/touchpoints-table/types";
import { collapseConsecutiveActivities } from "@/pages/accounts/account-explorer/touchpoints-table/utils";

type CollapsedDataAction =
  | { type: "SET_DATA"; data: Activity[] }
  | { type: "TOGGLE_EXPAND"; groupId: string };

/**
 * Custom hook to manage collapsed activity data state
 * Handles collapsing consecutive activities and toggling expanded state
 */
export function useCollapsedData(data: Activity[] = []) {
  // Use a reducer to manage collapsed data state
  const [collapsedData, dispatch] = useReducer(
    (state: (Activity | CollapsedActivity)[], action: CollapsedDataAction) => {
      switch (action.type) {
        case "SET_DATA": {
          // Rebuild collapsed groups from incoming data
          const rebuilt = collapseConsecutiveActivities(action.data);

          // Preserve expansion state for existing groups by id
          const prevExpandedById = new Map<string, boolean>();
          for (const item of state) {
            if ("activities" in item) {
              prevExpandedById.set(item.id, !!item.isExpanded);
            }
          }

          const merged = rebuilt.map((item) => {
            if ("activities" in item) {
              const prev = prevExpandedById.get(item.id);
              return typeof prev === "boolean"
                ? { ...item, isExpanded: prev }
                : item;
            }
            return item;
          });

          // If logically the same, return the old state reference to prevent downstream updates
          if (isEqual(merged, state)) {
            return state;
          }
          return merged;
        }
        case "TOGGLE_EXPAND":
          return state.map((item) => {
            if ("activities" in item && item.id === action.groupId) {
              return { ...item, isExpanded: !item.isExpanded };
            }
            return item;
          });
        default:
          return state;
      }
    },
    [],
  );

  // Update collapsed data when input data changes
  useEffect(() => {
    if (data && data.length > 0) {
      dispatch({ type: "SET_DATA", data });
    } else {
      dispatch({ type: "SET_DATA", data: [] });
    }
  }, [data]);

  // Function to toggle expand/collapse state of a group
  const toggleExpand = useCallback((groupId: string) => {
    dispatch({ type: "TOGGLE_EXPAND", groupId });
  }, []);

  return {
    collapsedData,
    toggleExpand,
  };
}
